// =============================================================================
// Protected Route Component - Con verifica Tenant
// =============================================================================

import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: Array<'OWNER' | 'ADMIN' | 'WORKER'>;
  /**
   * Richiede un'organizzazione attiva (default). Va messo a false solo per le
   * pagine che riguardano l'utente e non l'organizzazione — l'account
   * personale — che devono restare raggiungibili anche da chi sta ancora
   * scegliendo o creando la prima organizzazione.
   */
  requireTenant?: boolean;
}

export function ProtectedRoute({ children, requiredRoles, requireTenant = true }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, token, verifyToken } = useAuthStore();
  const [isVerifying, setIsVerifying] = useState(!!token);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        await verifyToken();
      }
      setIsVerifying(false);
    };

    checkAuth();
  }, []);

  // Loading state while verifying token
  if (isVerifying) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verifica sessione...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Nessun tenant selezionato - redirect alla selezione
  const activeTenantId = useTenantStore.getState().activeTenantId;
  if (requireTenant && !activeTenantId) {
    const memberships = useTenantStore.getState().memberships;
    if (memberships.length === 0) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/select-tenant" replace />;
  }

  // Check role permissions if specified
  if (requiredRoles && requiredRoles.length > 0) {
    const activeRole = useTenantStore.getState().activeRole;
    if (activeRole && !requiredRoles.includes(activeRole)) {
      return (
        <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
          <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg 
                className="w-8 h-8 text-red-600" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Accesso non autorizzato
            </h2>
            <p className="text-gray-600 mb-6">
              Non hai i permessi necessari per accedere a questa pagina.
              Contatta l'amministratore se ritieni sia un errore.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 btn-primary"
            >
              Torna indietro
            </button>
          </div>
        </div>
      );
    }
  }

  // Authenticated and authorized
  return <>{children}</>;
}

export default ProtectedRoute;
